import type { JSX } from 'react';
import { useParams } from 'react-router-dom';
import DSBox from '../../../design-system/DSBox';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import useResponsiveClockScale from '../../PublicKlydo/hooks/useResponsiveClockScale';
import usePublicCollectionQuery from '../hooks/usePublicCollectionQuery';
import KlydoPreview from '../../../components/KlydoPlayer/Wrappers/KlydoPreview';
import { DSTypography } from '../../../design-system/Typography';
import usePublicKlydoView from '../../PublicKlydo/hooks/usePublicKlydoView';
import { SHARE_PAGE_BACKGROUND } from '../../../consts/colors';

const commonBoxStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: SHARE_PAGE_BACKGROUND,
  overflowX: 'hidden',
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: {
    xs: '8px 16px',
    sm: '40px',
    md: '60px',
  },
} as const;

const PublicCollectionKlydoView = (): JSX.Element => {
  const { collectionId, klydoId } = useParams<{
    collectionId: string;
    klydoId: string;
  }>();

  const { data: collection, isLoading: collectionLoading } =
    usePublicCollectionQuery(collectionId || '');

  const scale = useResponsiveClockScale();
  const isLoading = collectionId ? collectionLoading : false;
  const klydo = collection?.klydos.find((k) => k.id === klydoId);

  const { menuButtonItemsList, isMuted, playSoundOnce, replaySoundTrigger } =
    usePublicKlydoView(
      klydo?.capabilitiesV2?.animationSound?.mode,
      !!klydo?.chimesUrls?.length,
      klydo?.chimesUrls?.[0],
    );

  if (isLoading) {
    return (
      <DSBox sx={commonBoxStyles}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (!klydo) {
    return (
      <DSBox
        sx={{
          ...commonBoxStyles,
          color: '#FFFFFF',
          fontSize: {
            xs: '14px',
            sm: '16px',
          },
          textAlign: 'center',
        }}
      >
        <DSTypography>{'Klydo not found'}</DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={commonBoxStyles}>
      <KlydoPreview
        klydo={klydo}
        scale={scale}
        pendulumLogo={collection?.logoUrl}
        // This view only ever shows approved collections - logoUrl already
        // arrives pre-sized, so no client-side scaling should run on it.
        isApprovedCollection
        backgroundHidden={false}
        dialsHidden
        showInfoTabs
        menuButtonItemsList={menuButtonItemsList}
        muted={isMuted}
        playSoundOnce={playSoundOnce}
        replaySoundTrigger={replaySoundTrigger}
      />
    </DSBox>
  );
};

export default PublicCollectionKlydoView;
